/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AppError } from "@tivocorp/data-domain-lib";

import { ErrorCodeUtil } from "../utility/ErrorCodeUtil";
import { AxiosErrorAdapter, IErrorAdapter } from "./adapters";

/** Shape a raw thrown value may match, as far as this mapper cares. */
interface ErrorLike {
  message?: unknown;
  getMessage?: () => unknown;
}

/** Narrows `unknown` to the subset of {@link ErrorLike} present on it. */
function asErrorLike(error: unknown): ErrorLike {
  return typeof error === "object" && error !== null
    ? (error as ErrorLike)
    : {};
}

/**
 * Shared machinery for screen-level error mappers that produce a single
 * catalog `AppError` (e.g. A716, A707) with an optional sub-error-code
 * suffix resolved by {@link ErrorCodeUtil}.
 */
export abstract class BaseErrorMapper {
  protected static readonly adapter: IErrorAdapter = new AxiosErrorAdapter();

  /**
   * Sub-error suffix for a raw error, resolved by the shared
   * {@link ErrorCodeUtil} so every screen reports the same failure the same
   * way (N6xx transport, S6xx service context, G4xx-G6xx HTTP status, or
   * empty when no layer recognises it).
   */
  protected static extractSubErrorCode(error: unknown): string {
    return ErrorCodeUtil.extractSubErrorCode(error, this.adapter);
  }

  /**
   * Best available diagnostic text for a thrown error: an `AppError`'s
   * message — those do not extend `Error`, so `error.message` alone would
   * miss them — then a plain `Error.message`, then empty.
   */
  protected static extractMessage(error: unknown): string {
    const errorLike: ErrorLike = asErrorLike(error);
    if (typeof errorLike.getMessage === "function") {
      const message: unknown = errorLike.getMessage();
      if (typeof message === "string" && message.length > 0) {
        return message;
      }
    }
    return typeof errorLike.message === "string" ? errorLike.message : "";
  }

  protected static buildAppError(
    code: string,
    message: string,
    subErrorCode: string | undefined,
    title: string,
    description: string
  ): AppError {
    return new AppError(code, message, subErrorCode, title, description);
  }
}
